import { useBusinessService } from '@ingenico/react-section-utils';
import { requestAndUpdateState } from '@ingenico/react-utils/functions';
import { useCallback } from 'react';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';
import type { ResourceListItemFront } from '../domain/models/resources.model';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import type { CoreError } from '@ingenico/api-sdk-core/common';
import { mapResourceListItemFrontToUpdateRequest } from '../components/resources-list/ResourcesList.util';

export interface UseTriggerEditResourceRequestParam {
  readonly onError?: (error: CoreError) => void;
  readonly onSuccess?: (resource: ResourceListItemFront) => void;
}

export function useTriggerEditResourceRequest({ onError, onSuccess }: UseTriggerEditResourceRequestParam) {
  const deviceManagementService = useBusinessService(DeviceManagementService);
  const [editResourceState, setEditResourceState] = useAsyncDataState<ResourceListItemFront, CoreError>();

  const editResource = useCallback(
    (resource?: ResourceListItemFront) => {
      if (resource) {
        requestAndUpdateState(
          deviceManagementService.editResource(resource.id, mapResourceListItemFrontToUpdateRequest(resource)),
          setEditResourceState,
          {
            onError,
            onSuccess,
          },
        );
      }
    },
    [deviceManagementService, onError, onSuccess, setEditResourceState],
  );

  const reset = useCallback(
    () => setEditResourceState({ isLoading: false, isError: false, isSuccess: false, error: null, data: null }),
    [setEditResourceState],
  );

  return {
    editResource,
    reset,
    isLoading: editResourceState.isLoading,
    error: editResourceState.error,
  };
}
